import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { EASE, IMG, scrollToHash } from "./shared";

const LINES = ["Votre bien entretenu.", "Votre quotidien simplifié."];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.9], [1, 0.2]);

  return (
    <section ref={ref} className="relative flex min-h-[96vh] items-end overflow-hidden bg-nuit" data-testid="hero-section">
      <motion.div style={{ y }} className="absolute inset-0 scale-110">
        <img src={IMG.hero} alt="Immeuble résidentiel moderne en Valais" fetchPriority="high" className="h-full w-full object-cover" />
      </motion.div>
      <div className="absolute inset-0 bg-nuit/60" />
      <div className="absolute inset-x-0 bottom-0 h-40 bg-nuit/40 backdrop-blur-[2px] [mask-image:linear-gradient(to_top,black,transparent)]" />

      <motion.div style={{ opacity }} className="relative z-10 mx-auto w-full max-w-7xl px-5 pb-24 pt-40 lg:px-10 lg:pb-32">
        <div className="overflow-hidden">
          <motion.p
            initial={{ y: "110%" }}
            animate={{ y: "0%" }}
            transition={{ duration: 0.9, delay: 0.35, ease: EASE }}
            className="mb-6 text-[11px] font-semibold uppercase tracking-[0.3em] text-lagoon sm:text-xs"
          >
            Conciergerie • Entretien • Nettoyage • Travaux • Débarras — Valais central
          </motion.p>
        </div>

        <h1 className="font-display max-w-4xl text-4xl font-medium leading-[1.04] tracking-tight text-white sm:text-6xl lg:text-7xl">
          {LINES.map((line, i) => (
            <span key={line} className="block overflow-hidden pb-1">
              <motion.span
                className="block"
                initial={{ y: "115%" }}
                animate={{ y: "0%" }}
                transition={{ duration: 1.1, delay: 0.5 + i * 0.16, ease: EASE }}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.1, ease: EASE }}
          className="mt-7 max-w-xl text-base leading-relaxed text-white/80 sm:text-lg"
        >
          Un interlocuteur polyvalent pour prendre soin de votre immeuble, de votre PPE,
          de vos locaux ou de votre propriété.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.3, ease: EASE }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <a
            href="#devis"
            onClick={scrollToHash("#devis")}
            data-testid="hero-cta-devis"
            className="group inline-flex items-center gap-2 rounded-full bg-lagoon px-7 py-4 text-sm font-bold uppercase tracking-[0.12em] text-nuit transition-all duration-300 hover:bg-white"
          >
            Demander un devis
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
          <a
            href="#services"
            onClick={scrollToHash("#services")}
            data-testid="hero-cta-services"
            className="inline-flex items-center gap-2 rounded-full border border-white/30 px-7 py-4 text-sm font-semibold uppercase tracking-[0.12em] text-white transition-colors duration-300 hover:border-lagoon hover:text-lagoon"
          >
            Découvrir nos services
          </a>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2, duration: 1 }}
        className="absolute bottom-8 right-8 hidden text-white/50 lg:block"
      >
        <ArrowDown className="h-5 w-5 animate-bounce" />
      </motion.div>
    </section>
  );
};
